import { batch, computed, signal } from "@preact/signals-react"

// Table data — paginated server-side, unlike Products Review, so `tableData`
// only ever holds the current page and `totalCount` is the real population.
export const tableData = signal([])
export const totalCount = signal(0)
export const currentPage = signal(0)
export const pageSize = signal(50)

// Coverage strip: the % per field, over every row the filters match — NOT over
// the page. `coverageTotal` is the population those percentages are about, and
// the strip has to show it or two people read the same numbers differently.
export const coverage = signal(null)
export const coverageTotal = signal(0)

// Image viewer — { urls, index, title } or null. A whole gallery, not one
// image, so a product's six photos are something you page through. Local rather
// than the app-wide `imageModalData`, which renders at natural size and knows
// nothing about sets.
export const imageViewer = signal(null)

// Details drawer — one fetch per row opened, never preloaded: the text and the
// media URLs were ~66 % of the payload when the DIPI app shipped them inline.
export const detailsRow = signal(null)
export const detailsData = signal(null)
export const isLoadingDetails = signal(false)
// Which tab it opens on. The sellers chip goes straight to Offers.
export const detailsTab = signal(0)

// Marketplace offers per row, keyed "siteID:productID", and only for the
// products that have more than one seller.
//
// Its own request, fired once per page AFTER the table is on screen — never
// part of /search, which stays one table and one index. DIPI_LAST_ITEM_RAW
// keeps the last capture written, so on a marketplace the row shows whichever
// seller was scraped last: product 1566303504 on Otto.de read "DS Trading GmbH
// — 69,90 €" while the buybox that day was OTTO at 16,49 €.
export const offerSummary = signal({})

// Loading states
export const isLoadingFilters = signal(false)
export const isLoadingTable = signal(false)

// True once a search has been run. The table's own toolbar re-queries on its
// own (text and quality filters), and this is what stops it from firing before
// a customer and a site have been picked — `totalCount` cannot say it, because
// a search that legitimately returns nothing leaves it at 0.
export const hasSearched = signal(false)

// Filters
export const selectedCustomer = signal(null)   // { value, label } — REQUIRED
export const customerOptions = signal([])
export const selectedMarkets = signal([])
// Sites, several allowed, at least one required. It is the second column of
// idx_customer_site_brand and what bounds the query — without it a search is
// the customer's whole catalogue, up to 200.217 products, and both the coverage
// aggregate and the sort are paid over that. The market filter only narrows the
// list to choose from. See `canSearch`.
export const selectedSites = signal([])        // [{ value, label, countryCode }]
export const sitesOptions = signal([])
export const selectedBrands = signal([])       // normalised brands (id_brand)
export const searchText = signal('')

// The column the table is sorted by — { column, direction } or null for the
// backend's default, which is the index prefix (id_site, brand, product_id).
// Sorting is applied by the BACKEND: the grid could only order the 50 rows on
// screen, and the whole point is to find the extremes across 48.666.
export const sortOrder = signal(null)

// Which columns the user has shown or hidden — { columnName: boolean }.
// Lives here rather than inside the grid because the column definitions are
// rebuilt on every response (the labels carry the coverage percentages), and a
// rebuilt definition resets `display` to its default. Without this, toggling a
// column and then searching again silently put it back.
//
// Cleared on leaving the page, like every other signal here.
export const columnDisplay = signal({})

// "Has X?" filters — { key: true | false }, a key absent meaning "do not
// filter". Applied by the BACKEND over the whole filtered set, not by the grid:
// the DIPI app puts the same controls behind the table's funnel, but that panel
// only sees the loaded rows, which here are 50 of 48.666.
export const qualityFlags = signal({})

// Rolling window — same four signals, names and defaults as Products Review.
export const recentDataEnabled = signal(true)
export const recentDataDays = signal(30)
export const recentDataDateFrom = signal(null)
export const recentDataDateTo = signal(null)

// What the last Search actually asked for — the filter bar's controls frozen
// at the moment it was pressed.
//
// Every re-query (paging, sorting, the toolbar's search and quality filters)
// sends THIS, never the live controls. Reading the live ones meant picking a
// different site and then sorting a column quietly swapped the result for that
// site without a Search, and the table's own title moved with the date filter
// while the rows underneath were still the old ones.
export const appliedFilters = signal(null)

export const filtersValue = computed(() => ({
    customer: selectedCustomer.value?.value ?? null,
    sites: (selectedSites.value || []).map(site => site?.value ?? site),
    sort: sortOrder.value,
    brands: (selectedBrands.value || []).map(b => b?.value ?? b),
    text: searchText.value || '',
    flags: qualityFlags.value || {},
    recentDataEnabled: recentDataEnabled.value,
    recentDataDays: recentDataDays.value,
    recentDataFrom: recentDataDateFrom.value || null,
    recentDataTo: recentDataDateTo.value || null,
}))

// Neither the customer nor the site is optional. The customer because the
// backend keys one row per (product, site, customer), so without it every
// product appears once per customer and every percentage counts it several
// times. The site because a customer-wide query is up to 200.217 products.
export const canSearch = computed(
    () => selectedCustomer.value?.value != null && (selectedSites.value || []).length > 0
)

export const showDetails = computed(() => detailsRow.value !== null)

export const clearScrapingDataReviewSignals = () => batch(() => {
    tableData.value = []
    totalCount.value = 0
    coverage.value = null
    coverageTotal.value = 0
    currentPage.value = 0
    pageSize.value = 50
    detailsRow.value = null
    detailsData.value = null
    detailsTab.value = 0
    isLoadingDetails.value = false
    offerSummary.value = {}
    imageViewer.value = null
    isLoadingFilters.value = false
    isLoadingTable.value = false
    hasSearched.value = false
    appliedFilters.value = null
    selectedCustomer.value = null
    selectedMarkets.value = []
    selectedSites.value = []
    selectedBrands.value = []
    searchText.value = ''
    columnDisplay.value = {}
    sortOrder.value = null
    qualityFlags.value = {}
    recentDataEnabled.value = true
    recentDataDays.value = 30
    recentDataDateFrom.value = null
    recentDataDateTo.value = null
})
